import React from 'react';
import {
  ArrowUpRightIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import { LoadingSpinner } from 'lib/components/LoadingSpinner';
import { ActionsPopover } from 'lib/components/ActionsPopover';
import { ColumnButton } from 'lib/components/ColumnButton';
import { useNavigate } from 'react-router-dom';
import { useSafeLoading } from 'lib/components/hooks/useSafeLoading';
import { Product } from 'lib/enums/Product';
import { OrderModel } from 'lib/model/objects/orderModel';
import { ColumnService } from 'lib/services/directory';
import { getFirebaseContext } from 'utils/firebase';
import { selectUser } from 'redux/auth';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import ToastActions from 'redux/toast';
import { getDynamicRoute } from 'lib/frontend/utils/router';
import { PRODUCT_TO_ROUTES } from 'router/routes';
import { safeGetModelFromRef } from 'lib/model/getModel';
import { logAndCaptureException } from 'utils';
import { duplicateOrder } from './place/helpers/duplicateOrder';

type OrderActionOptionProps = {
  onClick: () => void;
};

function AdsTableActionsColumn({ onClick }: OrderActionOptionProps) {
  return (
    <ColumnButton
      type="button"
      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onClick();
      }}
      buttonText="Duplicate"
      startIcon={<DocumentDuplicateIcon className="w-4 h-4" />}
      border="none"
    />
  );
}

function OpenAdAction({ onClick }: OrderActionOptionProps) {
  return (
    <ColumnButton
      type="button"
      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onClick();
      }}
      buttonText="Open in new tab"
      startIcon={<ArrowUpRightIcon className="w-4 h-4" />}
      border="none"
    />
  );
}

type OrderTableRowActionsProps = {
  orderId: string;
};

export default function OrderTableRowActions({
  orderId
}: OrderTableRowActionsProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);

  const [orderIsDuplicating, handleDuplicateOrderClick] = useSafeLoading({
    callback: async (orderId: string) => {
      if (!user) {
        return;
      }

      const orderRef = getFirebaseContext().ordersRef().doc(orderId);

      const [orderModelError, orderModel] = await safeGetModelFromRef(
        OrderModel,
        getFirebaseContext(),
        orderRef
      );
      if (orderModelError) {
        logAndCaptureException(
          ColumnService.ORDER_MANAGEMENT,
          orderModelError,
          'Error getting order model',
          { orderId }
        );
        return;
      }

      const [newOrderRefError, newOrderRef] = await duplicateOrder(
        orderModel,
        user
      );

      if (newOrderRefError) {
        logAndCaptureException(
          ColumnService.ORDER_MANAGEMENT,
          newOrderRefError,
          'Error duplicating order',
          { orderId }
        );
        dispatch(
          ToastActions.toastError({
            headerText: 'Error',
            bodyText: 'Error duplicating order. Please try again.'
          })
        );
        return;
      }

      navigate(
        getDynamicRoute(PRODUCT_TO_ROUTES[orderModel.modelData.product].PLACE, {
          id: newOrderRef.id
        })
      );
    },
    errorConfig: {
      service: ColumnService.ORDER_MANAGEMENT,
      message: 'Error duplicating order',
      tags: {
        orderId
      }
    }
  });

  const handleOpenClick = async (orderId: string) => {
    const orderRef = getFirebaseContext().ordersRef().doc(orderId);
    const [orderModelError, orderModel] = await safeGetModelFromRef(
      OrderModel,
      getFirebaseContext(),
      orderRef
    );

    if (orderModelError) {
      logAndCaptureException(
        ColumnService.ORDER_MANAGEMENT,
        orderModelError,
        'Error getting order model',
        { orderId }
      );
      return;
    }

    const path =
      orderModel.modelData.product === Product.Obituary
        ? 'obituary'
        : 'classified';

    window.open(`/${path}/${orderId}`, '_blank');
  };

  if (orderIsDuplicating) {
    return (
      <div className="ml-8">
        <LoadingSpinner inline size="sm" />
      </div>
    );
  }

  return (
    <ActionsPopover id={`actions-popover-${orderId}`}>
      <OpenAdAction onClick={() => handleOpenClick(orderId)} />
      <AdsTableActionsColumn
        onClick={() => handleDuplicateOrderClick(orderId)}
      />
    </ActionsPopover>
  );
}
